import { useState } from "react";
import PropTypes from "prop-types";
import MoreOptions from "@/components/specific/More_Options";
import Delete from "@/components/common/alerts/Delete";
import ModifySubEvent from "@/components/shared/dialogs/Sub_Event_Dialog/Modify_Sub_Event";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import { useDispatch } from "react-redux";
import { deleteSubEvent } from "@/store/redux/slices/event/sub_event_slice";
import { useLoader } from "@/hooks/LoaderProvider";

const SubEventHeader = ({ subEvent, onDeleteSuccess, onModifySuccess }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModifyDialogVisible, setIsModifyDialogVisible] = useState(false);
  const [mode, setMode] = useState("modify");
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteProceed = async () => {
    showLoader();
    setIsDeleteModalOpen(false);
    try {
      await dispatch(deleteSubEvent(subEvent._id)).unwrap();
      onDeleteSuccess();
    } catch (error) {
      console.error("Failed to delete sub event:", error);
    } finally {
      hideLoader();
    }
  };

  const handleModify = () => {
    setIsModifyDialogVisible(true);
    setMode("modify");
  };

  const handleModifySuccess = () => {
    setIsModifyDialogVisible(false);
    onModifySuccess();
  };

  // Add menu configuration for MoreOptions
  const moreOptionsMenu = {
    items: [
      {
        key: "modify",
        label: "MODIFY",
        onClick: handleModify,
      },
      {
        key: "delete",
        label: "DELETE",
        danger: true,
        onClick: handleDelete,
      },
    ],
  };

  // Transform the subEvent data to match the expected structure
  const formattedSubEvent = {
    _id: subEvent?._id,
    name: subEvent?.name, // API returns 'name' directly
    description: subEvent?.description,
    location: {
      type: subEvent?.location?.venue, // API returns location.venue
      address: subEvent?.location?.address,
      virtualService: "", // Add if needed
      url: subEvent?.location?.url,
    },
    duration: {
      startDate: subEvent?.duration?.startDate,
      endDate: subEvent?.duration?.endDate,
      startTime: subEvent?.duration?.startTime,
      endTime: subEvent?.duration?.endTime,
    },
    members: subEvent?.members,
    schedule: subEvent?.schedule,
    manifestoID: subEvent?.manifestoID,
    eventID: subEvent?.eventID,
    organizationID: subEvent?.organizationID,
    logistics: subEvent?.logistics,
  };

  return (
    <div className="mt-4 sm:mt-6">
      <div className="flex flex-col w-full">
        <div className="flex flex-col justify-between w-full">
          <div className="flex flex-row justify-between items-start">
            <div className="flex text-left flex-row">
              <div className="mr-2 mt-2">
                <WebIcon
                  icon="BlueCalendar"
                  className="w-[60px] h-[60px] border shadow-sm rounded-lg bg-white p-2"
                />
              </div>
              <div>
                <h1 className="font-bold heading-large text-black">
                  {formattedSubEvent?.name}
                </h1>

                <div className="mt-4 space-y-2">
                  <p className="flex items-center text-regular text-black sm:text-base">
                    <WebIcon
                      icon="BlueLocation"
                      size="large"
                      className="w-[30px] h-[30px] mr-2 bg-white border shadow-sm rounded-lg p-1"
                    />
                    {formattedSubEvent?.location?.address}
                  </p>

                  {formattedSubEvent?.location?.url && (
                    <p className="flex items-center text-regular text-black sm:text-base">
                      <WebIcon
                        icon="BlueVideo"
                        size="large"
                        className="w-[30px] h-[30px] mr-2 bg-white border shadow-sm rounded-lg p-1"
                      />
                      <a
                        href={formattedSubEvent?.location?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline">
                        Join Google Meet
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="lg:hidden">
              <MoreOptions menu={moreOptionsMenu} />
            </div>
            <div className="items-center hidden gap-3 lg:flex">
              <Button
                text="DELETE"
                color="outline"
                className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px]"
                customBgColor=""
                customTextColor="red"
                onClick={handleDelete}
              />
              <Button
                text="MODIFY"
                color="primary"
                className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px]"
                customBgColor="primary"
                customTextColor="white"
                onClick={handleModify}
              />
            </div>
          </div>
        </div>
      </div>
      <Delete
        isVisible={isDeleteModalOpen}
        onClose={handleCloseDelete}
        onProceed={handleDeleteProceed}
        header="Delete Sub Event?"
        paragraph="Are you sure you want to delete this sub event?"
        proceedButtonText="Proceed"
        cancelButtonText="Cancel"
      />
      <ModifySubEvent
        isVisible={isModifyDialogVisible}
        onClose={() => setIsModifyDialogVisible(false)}
        mode={mode}
        subEvent={formattedSubEvent}
        onSuccess={handleModifySuccess}
        onDeleteSuccess={onDeleteSuccess}
      />
    </div>
  );
};

SubEventHeader.propTypes = {
  subEvent: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.shape({
      venue: PropTypes.string,
      address: PropTypes.string,
      url: PropTypes.string,
      _id: PropTypes.string,
    }),
    duration: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    }),
    members: PropTypes.array,
    schedule: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    manifestoID: PropTypes.string,
    eventID: PropTypes.string,
    organizationID: PropTypes.string,
    logistics: PropTypes.array,
  }),
  onDeleteSuccess: PropTypes.func,
  onModifySuccess: PropTypes.func,
};

export default SubEventHeader;
